import React, { useContext, useState } from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import type { DateRangeFormatFunction, stringOrDate } from 'react-big-calendar';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import type { Moment } from 'moment';
import moment from 'moment';
import Color from 'color';
import Event, { convertCandidateToArgs, convertTravelTimeToArgs } from '@this/domain/appoint/event';
import type Adjustment from '@this/domain/appoint/adjustment';
import Candidate, { travelTimeTitle } from '@this/domain/appoint/candidate';
import type Calendar from '@this/domain/appoint/calendar';
import type SlotInfo from '@this/domain/appoint/slot_info';
import { CalendarWrapper, CalendarLoading, formats, messages } from '@this/components/appoint/common';
import AppointContext from '@this/components/appoint/appoint_context';
import EventPopupCard from '@this/components/appoint/shared/event_popup_card/event_popup_card';
import muiTheme from '@this/components/appoint/mui_theme';

const DragAndDropCalendar = withDragAndDrop(BigCalendar);

type EventInfo = { event: Event; start: stringOrDate; end: stringOrDate; allDay: boolean };

const localizer = momentLocalizer(moment);

interface Props {
  adjustment: Adjustment;
  eventLists: Event[][];
  candidates: Candidate[];
  travelTimes: Candidate[];
  calendars: Calendar[];
  date: Moment;
  isEventLoading: boolean;
  onCandidatesChange: (candidates: Candidate[], travelTimes?: Candidate[]) => void;
  onDateChange: (date: Moment) => void;
}

const NewBigCalendar = ({
  adjustment,
  eventLists,
  candidates,
  travelTimes,
  calendars,
  date,
  isEventLoading,
  onCandidatesChange,
  onDateChange
}: Props) => {
  const contextValue = useContext(AppointContext);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [eventClickX, setEventClickX] = useState<number>(0);
  const [eventClickY, setEventClickY] = useState<number>(0);
  const [calendarScrollTo, setCalendarScrollTo] = useState<Date | undefined>(
    moment().set({ hour: 5, minute: 50 }).toDate()
  );
  const [selectedCalendarView, setSelectedCalendarView] = useState<'week' | 'day'>('week');
  const [touchStartedX, setTouchStartedX] = useState<number>(0);
  const [touchMovedX, setTouchMovedX] = useState<number>(0);
  const [isLongTapInfoShown, setIsLongTapInfoShown] = useState<boolean>(false);

  if (contextValue.isMobile) {
    const dayRangeHeaderFormat: DateRangeFormatFunction = ({ start }) => {
      const s = moment(start);
      return s.format('YYYY年MM月DD日(ddd) 週');
    };
    formats.dayRangeHeaderFormat = dayRangeHeaderFormat;
  }

  let events: Event[] = [];
  if (eventLists.length > 0) {
    events = eventLists.flat();
  }
  events = [
    ...events,
    ...candidates
      .map(c => {
        return new Event(
          convertCandidateToArgs(c, contextValue.isMobile ? '候補日（ロングタップで削除）' : undefined)
        );
      })
      .flat(),
    ...travelTimes
      .map(c => {
        return new Event(convertTravelTimeToArgs(c));
      })
      .flat()
  ];

  const showPastError = () => {
    contextValue.setSnackbarContent({ message: '過去日時は選択できません', severity: 'error' });
    contextValue.setOpenSnackbar(true);
  };

  const showShortError = () => {
    contextValue.setSnackbarContent({
      message: '打ち合わせ時間より短い候補には変更できません',
      severity: 'error'
    });
    contextValue.setOpenSnackbar(true);
  };

  const followCandidate = (
    travelTime: Candidate,
    oldStart: Moment,
    oldEnd: Moment,
    newStart: Moment,
    newEnd: Moment
  ) => {
    if (!travelTime.start.isSame(newStart, 'day')) {
      return null;
    }
    if (travelTime.end.isSame(oldStart)) {
      const diff = oldStart.diff(newStart);
      travelTime.start = travelTime.start.subtract(diff);
      travelTime.end = travelTime.end.subtract(diff);
    } else if (oldEnd.isSame(travelTime.start)) {
      const diff = oldEnd.diff(newEnd);
      travelTime.start = travelTime.start.subtract(diff);
      travelTime.end = travelTime.end.subtract(diff);
    }
    return travelTime;
  };

  // 既存の候補日と重複があればマージする
  const mergeCandidate = (start: Moment, end: Moment) => {
    for (let i = 0; i < candidates.length; i += 1) {
      const c = candidates[i];
      if ((c.start.isSame(end) || c.start.isBefore(end)) && (c.end.isSame(start) || c.end.isAfter(start))) {
        c.start = moment.min([c.start, start]);
        c.end = moment.max([c.end, end]);
        onCandidatesChange(candidates.sort((a, b) => a.start.diff(b.start)));
        return true;
      }
    }
    return false;
  };

  const handleSelectSlot = (slot: SlotInfo) => {
    setSelectedEvent(null);
    const tappedCandidate = _.find(
      candidates,
      c => c.start.format() <= moment(slot.start).format() && c.end.format() >= moment(slot.end).format()
    );
    if (tappedCandidate) {
      adjustment.removeCandidate(tappedCandidate);
      return;
    }
    if (slot.start === slot.end) {
      // 終日の選択は無視
      return;
    }

    if (moment(slot.start).isBefore(moment())) {
      showPastError();
      return;
    }

    // 会議時間に合わせてendを延長
    const durationMs = adjustment.duration * 60 * 1000;
    const start = moment(slot.start);
    let end = moment(slot.end);
    if (end.diff(start) < durationMs) {
      end = start.clone();
      end.add(adjustment.duration, 'minutes');
    }

    if (mergeCandidate(start, end)) {
      return;
    }

    // マージできる既存候補日がなければ作成
    const candidate = new Candidate({ start, end });
    onCandidatesChange([...candidates, candidate].sort((a, b) => a.start.diff(b.start)));
  };

  const handleDoubleClickEvent = (event: Event) => {
    if (contextValue.isMobile || !event.isCandidate) {
      return;
    }
    const candidate = _.find(candidates, c => c.start.isSame(moment(event.start)) && c.end.isSame(event.end));
    if (!candidate) {
      return;
    }
    adjustment.removeCandidate(candidate);
  };

  const handleCandidateChange = (info: EventInfo) => {
    const startTime = moment(info.start);
    const endTime = moment(info.end);
    if (startTime.isBefore(moment())) {
      showPastError();
      return;
    }
    if (endTime.diff(startTime, 'minutes') < adjustment.duration) {
      showShortError();
      return;
    }

    const candidate = _.find(
      candidates,
      c => c.start.isSame(moment(info.event.start)) && c.end.isSame(info.event.end)
    );
    if (!candidate) {
      return;
    }
    const oldStart = candidate.start;
    const oldEnd = candidate.end;
    candidate.start = startTime;
    candidate.end = endTime;

    const newTravelTimes = travelTimes.reduce((acc: Candidate[], v: Candidate) => {
      if (v.start.isSame(oldStart, 'day')) {
        const followed = followCandidate(v, oldStart, oldEnd, candidate.start, candidate.end);
        if (followed) {
          acc.push(followed);
        }
      } else {
        acc.push(v);
      }
      return acc;
    }, []);
    onCandidatesChange(candidates, newTravelTimes);
  };

  const handleSelectEvent = (event: Event, e: React.SyntheticEvent<HTMLElement, globalThis.Event>) => {
    if (selectedEvent) {
      if (selectedEvent.id && selectedEvent.id === event.id) {
        setSelectedEvent(null);
        return;
      }
      if (selectedEvent.name === event.name) {
        setSelectedEvent(null);
        return;
      }
    }
    if (event.isCandidate) {
      setSelectedEvent(null);
      return;
    }
    setSelectedEvent(event);
    const click = e.nativeEvent as MouseEvent;
    setEventClickX(click.pageX - 550);
    setEventClickY(click.pageY - 100);
  };

  const getEventProp = (e: Event) => {
    const calendar = _.find(calendars, c => c.id === e.calendarId);
    const eventProps = {
      className: e.end.valueOf() - e.start.valueOf() < 1000 * 60 * 45 ? 'short_event' : '',
      style: {}
    };
    let backgroundColor = new Color(
      calendar && calendar.backgroundColor ? calendar.backgroundColor : muiTheme.palette.primary.main
    );
    if (e.end < moment()) {
      if (calendar && calendar.backgroundColor) {
        const lightness = backgroundColor.lightness();
        const newLightness = 100 - (100 - lightness) / 5;
        backgroundColor = backgroundColor.lightness(newLightness);
      } else {
        backgroundColor = backgroundColor.lighten(0.5);
      }
    }
    if (calendar && calendar.backgroundColor) {
      const color = new Color('#ffffff');
      eventProps.style = {
        backgroundColor: backgroundColor.hex(),
        color: calendar.foregroundColor || '#1d1d1d',
        borderColor: color.darken(0.0).hex()
      };
      return eventProps;
    }
    if (e.name === travelTimeTitle) {
      let color = new Color(muiTheme.palette.secondary.dark);
      if (e.end < moment()) color = color.lighten(0.3);
      eventProps.style = {
        backgroundColor: color.hex(),
        border: 0
      };
      return eventProps;
    }
    // 候補日は破線ボーダー
    if (contextValue.isMobile) {
      eventProps.className += ' adjustment-candidate-event';
    }
    eventProps.style = {
      backgroundColor: '#fff',
      color: muiTheme.palette.primary.main,
      border: `dashed 2px ${muiTheme.palette.primary.main}`
    };
    return eventProps;
  };

  const isEditable = (info: Event) => {
    return info.isCandidate;
  };

  const handleRangeChange = (val: any) => {
    setSelectedCalendarView(val.length === 1 ? 'day' : 'week');
    setCalendarScrollTo(moment().set({ hour: 5, minute: 50 }).toDate());
  };

  const handleTouchStart = (e: any) => {
    setTouchStartedX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: any) => {
    setTouchMovedX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartedX !== 0 && touchMovedX !== 0) {
      const diff = selectedCalendarView === 'day' ? 1 : 7;
      if (touchStartedX - touchMovedX > 150) {
        onDateChange(date.add(diff, 'days'));
      }

      if (touchStartedX - touchMovedX < -150) {
        onDateChange(date.subtract(diff, 'days'));
      }
    }
    setTouchStartedX(0);
    setTouchMovedX(0);
  };

  const handleCalendarMarginTap = () => {
    if (!contextValue.isMobile || isLongTapInfoShown) {
      return;
    }

    contextValue.setSnackbarContent({
      message: 'カレンダーの空欄をロングタップで候補日を追加',
      severity: 'info'
    });
    contextValue.setOpenSnackbar(true);
    setIsLongTapInfoShown(true);
  };

  return (
    <>
      <EventPopupCard
        event={selectedEvent}
        x={eventClickX}
        y={eventClickY}
        onClose={() => setSelectedEvent(null)}
      />
      <TheCalendarWrapper
        isMobile={contextValue.isMobile}
        onClick={() => handleCalendarMarginTap()}
        onTouchStart={e => handleTouchStart(e)}
        onTouchMove={e => handleTouchMove(e)}
        onTouchEnd={() => handleTouchEnd()}
      >
        {isEventLoading && <CalendarLoading />}
        <DragAndDropCalendar
          localizer={localizer}
          events={events}
          titleAccessor="name"
          startAccessor={event => event.start.toDate()}
          endAccessor={event => event.end.toDate()}
          defaultView="week"
          views={['day', 'week']}
          selectable
          longPressThreshold={300}
          onRangeChange={val => handleRangeChange(val)}
          onSelectEvent={(event, e) => handleSelectEvent(event, e)}
          onDoubleClickEvent={event => handleDoubleClickEvent(event)}
          onSelectSlot={slot => handleSelectSlot(slot)}
          resizable
          onEventResize={info => handleCandidateChange(info)}
          onEventDrop={info => handleCandidateChange(info)}
          draggableAccessor={info => isEditable(info)}
          eventPropGetter={e => getEventProp(e)}
          date={date.toDate()}
          onNavigate={date => {
            setSelectedEvent(null);
            onDateChange(moment(date));
          }}
          formats={formats}
          scrollToTime={calendarScrollTo}
          messages={messages}
        />
      </TheCalendarWrapper>
    </>
  );
};

const TheCalendarWrapper = styled(CalendarWrapper)<{ isMobile: boolean }>`
  ${props =>
    props.isMobile &&
    `
    height: calc(100vh - 30px - 20px - 46px - 77px);
    padding-bottom: 77px;

    .rbc-toolbar {
      padding: 0 10px;
    }
  `}
`;

export default NewBigCalendar;
